<!--
  --
  -- 日付入力コンポーネント
  --
  -- 日付情報はすべて文字列で扱う。
  -- そのため、不正な日付文字列 (6月31日 など)も許容する。
  -- 日付文字列の妥当性チェックは rules の定義で行うことを強く勧める。
  --
  -->
<template>
  <c-input
    class="c-date-field"
    :label="label"
    :value="value"
    :rules="rules"
  >
    <div class="d-flex flex-nowrap w-full">
      <v-select
        class="c-date-field-year"
        hide-details
        v-model="year"
        :items="yearItems"
      />
      <v-select
        class="c-date-field-month"
        hide-details
        v-model="month"
        :items="monthItems"
      />
      <v-select
        class="c-date-field-day"
        hide-details
        v-model="day"
        :items="dayItems"
      />
    </div>
  </c-input>
</template>

<script>
import CInput from './CInput'

import * as time from '@/libs/time'

export default {
  name: 'CDateField',
  components: {
    CInput
  },

  // v-model の定義
  model: {
    prop: 'value',
    event: 'change'
  },

  props: {
    // ラベル
    label: {
      type: String
    },

    // 値
    value: {
      type: String
    },

    // 入力チェックルール
    rules: {
      type: Array,
      default: () => []
    },

    // 入力可能な最も古い年
    minYear: {
      type: [String, Number]
    },

    // 入力可能な最も新しい年
    maxYear: {
      type: [String, Number]
    },

    // オートフォーカス
    autofocus: {
      type: Boolean,
      default: false
    }
  },

  data: () => ({
    // 年月日 各項目の入力値
    year: '',
    month: '',
    day: '',

    // 月, 日の選択肢
    monthItems: MONTH_ITEMS,
    dayItems: DAY_ITEMS
  }),

  computed: {
    /**
     * change イベントで返す日付データ
     */
    date () {
      if (!this.year || !this.month || !this.day) {
        return null
      }
      return this.year + '-' + this.month + '-' + this.day
    },

    /**
     * 年の選択肢
     */
    yearItems () {
      const min = num(this.minYear, 1900)
      const max = num(this.maxYear, min)
      const len = max - min + 1
      const years = new Array(len)
      for (let n = 0, y = max; y >= min; ++n, --y) {
        years[n] = {
          value: time.z4(y),
          text: time.format('Y/bk年', time.create({ y, m: 12, d: 31 }))
        }
      }

      return years
    }
  },

  watch: {
    // value プロパティが変化したら、reactValue() で year, month, day を更新する
    value: {
      immediate: true,
      handler: 'reactValue'
    },

    // date が変化したら、change イベントを送出する
    date (newValue) {
      this.$emit('change', newValue)
    }
  },

  methods: {
    /**
     * 日付文字列から year, month, day を更新する
     *
     * @param {String} value 日付文字列
     */
    reactValue (value) {
      const re = /^(?<y>\d{4})-(?<m>\d{2})-(?<d>\d{2})$/
      const m = value.match(re)
      if (m) {
        this.year = m.groups.y
        this.month = m.groups.m
        this.day = m.groups.d
      } else {
        this.year = ''
        this.month = ''
        this.day = ''
      }
    }
  }
}

/**
 * 値を数値に変換する
 *
 * @param {*} x 変換元の値
 * @param {Number} def デフォルト値
 * @return {Number} 変換された数値を返す
 */
function num (x, def = null) {
  return (x === null || isNaN(x - 0)) ? def : (x - 0)
}

const MONTH_ITEMS = [
  { value: '01', text: '1月' },
  { value: '02', text: '2月' },
  { value: '03', text: '3月' },
  { value: '04', text: '4月' },
  { value: '05', text: '5月' },
  { value: '06', text: '6月' },
  { value: '07', text: '7月' },
  { value: '08', text: '8月' },
  { value: '09', text: '9月' },
  { value: '10', text: '10月' },
  { value: '11', text: '11月' },
  { value: '12', text: '12月' }
]

const DAY_ITEMS = [
  { value: '01', text: '1日' },
  { value: '02', text: '2日' },
  { value: '03', text: '3日' },
  { value: '04', text: '4日' },
  { value: '05', text: '5日' },
  { value: '06', text: '6日' },
  { value: '07', text: '7日' },
  { value: '08', text: '8日' },
  { value: '09', text: '9日' },
  { value: '10', text: '10日' },
  { value: '11', text: '11日' },
  { value: '12', text: '12日' },
  { value: '13', text: '13日' },
  { value: '14', text: '14日' },
  { value: '15', text: '15日' },
  { value: '16', text: '16日' },
  { value: '17', text: '17日' },
  { value: '18', text: '18日' },
  { value: '19', text: '19日' },
  { value: '20', text: '20日' },
  { value: '21', text: '21日' },
  { value: '22', text: '22日' },
  { value: '23', text: '23日' },
  { value: '24', text: '24日' },
  { value: '25', text: '25日' },
  { value: '26', text: '26日' },
  { value: '27', text: '27日' },
  { value: '28', text: '28日' },
  { value: '29', text: '29日' },
  { value: '30', text: '30日' },
  { value: '31', text: '31日' }
]
</script>

<style lang="scss">
.c-date-field {
  width: 16.5rem;

  .v-select__selections {
    position: relative;

    .v-select__selection--comma {
      margin-right: 0;
      max-width: 100%;
    }

    input[type="text"][readonly="readonly"] {
      position: absolute;
      left: -9999px;
    }
  }

  %field {
    margin-top: 0;
    padding-top: 0;
  }

  .c-date-field-year {
    @extend %field;
    margin-right: .5rem;
    flex: 1 1;
  }
  .c-date-field-month {
    @extend %field;
    margin-right: .5rem;
    flex: 0 0;
    flex-basis: 4rem;
  }
  .c-date-field-day {
    @extend %field;
    flex: 0 0;
    flex-basis: 4rem;
  }
}
</style>
